// @ts-strict-ignore
import { AssigneeInputBlock } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { CardFormContext } from '@shared/components/blocks/contexts/form';
import { defaultFieldPresenter } from '@shared/components/blocks/lib/field';
import { fieldPresenter } from '@shared/components/blocks/lib/fieldAssignee';
import { useResolveAssignees } from '@shared/components/blocks/lib/fieldAssignee/hooks';
import { MBAssigneeUserIcon } from '@shared/components/blocks/lib/fieldAssignee/userIcon';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBChannelImage, MBUserImage } from '@shared/components/image';
import { MBMultiSelectModal } from '@shared/components/multiSelect';
import { MBTextBody1 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { MBAlert } from '@shared/util/alert';
import { useChannelUsers } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { getTypoComponent } from '../../utils';
import { MBCardInputBlockBase } from '../input/base';
import { renderClearButton } from '../input/utils';
import { BatchOptions } from './batchOptions';
import { useCreateBatchTagOnSubmit } from './hooks';

interface Props {
  block: AssigneeInputBlock;
}

export function keyExtractor(user: API.ShortUserFragment) {
  return String(user.id);
}

export function MultiSelectUserRow({ user }: { user: User }) {
  const colors = useContext(ColorThemeContext);
  return (
    <View row centerVertically paddingVertical={1}>
      <MBUserImage style={styles.userImage} user={user} />
      <View flex centerVertically>
        <MBTextBody1 numberOfLines={1} color={colors.foregroundActive}>
          {user.fullName}
        </MBTextBody1>
      </View>
    </View>
  );
}

export function MBCardInputBlockAssignee({ block }: Props) {
  const colors = useContext(ColorThemeContext);
  const channel = useContext(CardChannelContext);
  const { setFieldValue, values } = useContext(CardFormContext);
  const { users: channelUsers } = useChannelUsers({ channelId: channel?.id });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedUserIds = useMemo<number[]>(() => values[block.name] || [], [values[block.name]]);
  const { users: selectedUsers } = useResolveAssignees(selectedUserIds, channelUsers);
  const onChange = useCallback(
    (changedUsers: API.ShortUserFragment[]): void =>
      setFieldValue(block.name, isEmpty(changedUsers) ? null : changedUsers.map(u => u.id)),
    [block.name, setFieldValue],
  );

  const color = selectedUsers.length ? colors.foregroundActive : colors.foregroundInactive;
  const content = useMemo(
    () =>
      selectedUsers.length > 0
        ? fieldPresenter(selectedUsers, block.fieldType, block.textStyle, color)
        : defaultFieldPresenter(block.placeholder, block.fieldType, block.textStyle, color),
    [block, selectedUsers, color],
  );

  const renderLeft = useMemo(
    () => (!!selectedUsers.length ? () => <MBAssigneeUserIcon users={selectedUsers} /> : null),
    [selectedUsers],
  );
  const renderRight = useMemo(
    () => (!!selectedUsers.length ? renderClearButton : null),
    [selectedUsers],
  );
  const renderLabel = useMemo(
    // eslint-disable-next-line react/display-name
    () => () => {
      if (!selectedUsers.length) {
        return null;
      }
      if (values.batchByAssignee && block.batchLabel) {
        return <MBBlockLabel label={block.batchLabel} />;
      }

      if (!!block.label) {
        return <MBBlockLabel label={block.label} />;
      }

      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedUsers, values.batchByAssignee, block.batchLabel],
  );

  const match = useCallback((user: API.ShortUserFragment, query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return (
      user.username.toLowerCase().includes(lowercaseQuery) ||
      user.fullName.toLowerCase().includes(lowercaseQuery)
    );
  }, []);

  const updateBatchName = useCallback(
    (value: string) => setFieldValue('batchByAssigneeName', value),
    [setFieldValue],
  );

  const recurrenceEnabled = !!values.recurrence;
  const toggleBatchByAssignee = useCallback(
    async enabled => {
      if (enabled && recurrenceEnabled) {
        const confirmed = await MBAlert.confirm({
          title: i18n('card.blocks.assigneeInput.recurrenceAlert.title'),
          message: i18n('card.blocks.assigneeInput.recurrenceAlert.message'),
          confirmText: i18n('card.blocks.assigneeInput.recurrenceAlert.confirmText'),
        });

        if (!confirmed) {
          return;
        }
        setFieldValue('recurrence', null);
      }
      setFieldValue('batchByAssignee', enabled);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFieldValue, values, recurrenceEnabled],
  );

  useCreateBatchTagOnSubmit(block);

  const TypoComponent = getTypoComponent(block.textStyle);
  if (block.allowBatch && !block.batchTagFieldName) {
    return (
      <TypoComponent color={colors.error.foreground}>
        Warning: cannot render assignee input because allowBatch is enabled, but no
        batchTagFieldName has been defined in the input block.
      </TypoComponent>
    );
  }

  const anchorName = `--input-assignee-${block.name}`;

  return (
    <MBMultiSelectModal<API.ShortUserFragment>
      label={block.label}
      anchorName={anchorName}
      onChange={onChange}
      selectAll
      renderSelectAll={() => (
        <View flex row centerVertically paddingVertical={1}>
          {!isNil(channel) && <MBChannelImage channel={channel} style={styles.userImage} />}
          <MBTextBody1>{i18n('form.placeholder.everyone')}</MBTextBody1>
        </View>
      )}
      header={
        !!block.allowBatch && (
          <BatchOptions
            enabled={!!values.batchByAssignee}
            name={values.batchByAssigneeName ?? values.name}
            onToggle={toggleBatchByAssignee}
            onChangeName={updateBatchName}
          />
        )
      }
      max={block.validation?.max}
      renderItem={user => <MultiSelectUserRow user={user} />}
      values={channelUsers}
      selectedValues={selectedUsers}
      match={match}
      keyExtractor={keyExtractor}
    >
      <MBCardInputBlockBase
        block={block}
        renderLabel={renderLabel}
        renderLeft={renderLeft}
        renderRight={renderRight}
      >
        <View style={{ anchorName }}>{content}</View>
      </MBCardInputBlockBase>
    </MBMultiSelectModal>
  );
}

const styles = StyleSheet.create({
  userImage: {
    width: grid(5),
    height: grid(5),
    borderRadius: grid(2.5),
    overflow: 'hidden',
    marginRight: grid(2),
  },
});
